import React from "react";
import TitleBar from "../../components/navbar/TitleBar";
import SchoolBusNavMenu from "../../components/createForm/SchoolBusNavMenu";
import Footer from "../../components/common/Footer";
import SubTitle from "../../components/common/SubTitle";
import SearchFilter from "../../components/common/SearchFilter";
import { Suspense, useState, useMemo, useEffect } from "react";
import SchoolDynamicTable from "../../components/createForm/SchoolDynamicTable";
import CreateStopForm from "../../components/createForm/CreateStopForm";
import axios from "axios";
import config from "../../config.js";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

export default function SchoolBusStops() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [filterValues, setFilterValues] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [stopsData, setStopsData] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [routeOptions, setRouteOptions] = useState([]);
  const [stopOptions, setStopOptions] = useState([]);

  const headers = [
    { key: "Stop ID", value: "IDs" },
    { key: "Stop Name", value: "Names" },
    { key: "Routes", value: "Routes" },
    { key: "actions", value: "" },
  ];

  // Filter configuration - will be populated dynamically
  const filters = useMemo(
    () => [
      {
        id: "routes",
        label: "Routes",
        helperText: "Select route",
        options: routeOptions,
      },
      {
        id: "stopName",
        label: "Stop Name",
        helperText: "Select stop",
        options: stopOptions,
      },
    ],
    [routeOptions, stopOptions],
  );

  // Fetch stops data on component mount
  useEffect(() => {
    fetchStops();
  }, []);

  // Function to fetch routes by stop coordinates
  const getRouteByStopCoordinates = async (lat, lng, orgId) => {
    try {
      const response = await axios.get(`${config.baseURL}/route/coordinates/`, {
        params: {
          longitude: lng,
          latitude: lat,
          organizationId: orgId,
        },
        withCredentials: true,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching routes by coordinates:", error);
      return [];
    }
  };

  const fetchStops = async () => {
    setIsLoading(true);
    try {
      const stopsResponse = await axios.get(
        `${config.baseURL}/stop/organization`,
        { withCredentials: true },
      );

      if (!Array.isArray(stopsResponse.data)) {
        setStopsData([]);
        setIsLoading(false);
        return;
      }

      const stops = [...stopsResponse.data].sort((a, b) => {
        const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();

        if (aTime !== bTime) {
          return bTime - aTime;
        }

        return Number(b.id || 0) - Number(a.id || 0);
      });

      // Fetch routes for each stop
      const stopsWithRoutes = await Promise.all(
        stops.map(async (stop) => {
          const routes = await getRouteByStopCoordinates(
            stop.latitude,
            stop.longitude,
            stop.organizationId,
          );
          return { ...stop, routes };
        }),
      );

      // Extract unique routes for filter
      const uniqueRoutes = [];
      const routeIds = new Set();

      // Extract unique stops for filter
      const uniqueStops = [];
      const stopIds = new Set();

      stopsWithRoutes.forEach((stop) => {
        // Process routes
        if (stop.routes && stop.routes.length > 0) {
          stop.routes.forEach((route) => {
            const routeId = route.id || route.Id || route.ID;
            const routeName = route.name || route.Name || `Route ${routeId}`;

            if (routeId && !routeIds.has(routeId)) {
              routeIds.add(routeId);
              uniqueRoutes.push({
                value: routeId.toString(),
                label: routeName,
              });
            }
          });
        }

        // Process stops
        const stopId = stop.id || stop.Id || stop.ID;
        const stopName = stop.name || stop.Name || `Stop ${stopId}`;

        if (stopId && !stopIds.has(stopId)) {
          stopIds.add(stopId);
          uniqueStops.push({
            value: stopId.toString(),
            label: stopName,
          });
        }
      });

      setRouteOptions(uniqueRoutes);
      setStopOptions(uniqueStops);

      // Transform API data to match table format
      const transformedData = stopsWithRoutes.map((stop) => {
        let routesDisplay = "No Route";

        if (stop.routes && stop.routes.length > 0) {
          routesDisplay = stop.routes
            .map((route) => route.name || `Route ${route.id}`)
            .join(", ");
        }

        return {
          "Stop ID": stop.stopId || "N/A",
          "Stop Name": stop.name || "N/A",
          Routes: routesDisplay,
          fullData: stop,
        };
      });

      setStopsData(transformedData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching stops:", error);
      setIsLoading(false);

      if (error.response && error.response.status === 401) {
        navigate("/");
      }
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (values) => {
    setFilterValues(values);
  };

  const handleCreateStop = () => {
    setShowCreateForm(true);
    setExpandedRow(null);
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
  };

  const handleSubmitStop = async (formData) => {
    try {
      const userState = localStorage.getItem("userState");
      const parsedUserState = JSON.parse(userState);
      const orgId = parsedUserState.user.organizationId;

      const response = await axios.post(
        `${config.baseURL}/stop`,
        { ...formData, organizationId: orgId },
        { withCredentials: true },
      );

      if (response.status === 200 || response.status === 201) {
        // Refresh the stops list
        await fetchStops();
        setShowCreateForm(false);
        console.log("New stop added:", response.data);
      }
    } catch (error) {
      console.error("Error creating stop:", error);
      throw error;
    }
  };

  const handleUpdateStop = async (formData) => {
    if (expandedRow === null) return;

    try {
      const userState = localStorage.getItem("userState");
      const parsedUserState = JSON.parse(userState);
      const orgId = parsedUserState.user.organizationId;

      const response = await axios.put(
        `${config.baseURL}/stop`,
        { ...formData, organizationId: orgId },
        { withCredentials: true },
      );

      if (response.status === 200) {
        // Refresh the stops list
        await fetchStops();
        setExpandedRow(null);
        console.log("Stop updated:", response.data);
      }
    } catch (error) {
      console.error("Error updating stop:", error);
      throw error;
    }
  };

  const handleDeleteStop = async (row, rowIndex) => {
    try {
      const userState = localStorage.getItem("userState");
      const parsedUserState = JSON.parse(userState);
      const orgId = parsedUserState.user.organizationId;
      const userId = parsedUserState.user.id;

      const response = await axios.delete(`${config.baseURL}/stop`, {
        data: {
          id: row.fullData.id,
          organizationId: orgId,
          deletedBy: userId,
        },
        withCredentials: true,
      });

      if (response.status === 200) {
        // Refresh the stops list
        await fetchStops();
        setExpandedRow(null);
        console.log("Stop deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting stop:", error);
      // Handle error (show notification, etc.)
    }
  };

  const handleRowClick = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
    setShowCreateForm(false);
  };

  const handleCloseExpandedForm = () => {
    setExpandedRow(null);
  };

  // Filter data based on search and filters
  const filteredData = useMemo(() => {
    let filtered = [...stopsData];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((stop) => {
        const stopIdMatch = stop["Stop ID"].toLowerCase().includes(query);
        const stopNameMatch = stop["Stop Name"].toLowerCase().includes(query);
        const routesMatch = stop.Routes.toLowerCase().includes(query);

        return stopIdMatch || stopNameMatch || routesMatch;
      });
    }

    // Apply route filter
    if (filterValues.routes) {
      filtered = filtered.filter((stop) => {
        // Check if any route in the stop matches the selected route
        if (stop.fullData.routes && Array.isArray(stop.fullData.routes)) {
          return stop.fullData.routes.some(
            (route) => String(route.id) === String(filterValues.routes),
          );
        }
        return false;
      });
    }

    // Apply stop name filter
    if (filterValues.stopName) {
      filtered = filtered.filter((stop) => {
        const stopId = stop.fullData.id || stop.fullData.Id || stop.fullData.ID;
        return String(stopId) === String(filterValues.stopName);
      });
    }

    return filtered;
  }, [searchQuery, filterValues, stopsData]);

  // Custom expanded row renderer
  const renderExpandedRow = (row, rowIndex) => {
    return (
      <CreateStopForm
        onClose={handleCloseExpandedForm}
        onSubmit={handleUpdateStop}
        onDelete={() => handleDeleteStop(row, rowIndex)}
        initialData={row.fullData}
        isEdit={true}
      />
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100vh",
      }}
    >
      <Box>
        <TitleBar />
        <SchoolBusNavMenu />
        <SubTitle
          title="Stops"
          add="+ Create Stops"
          onAddClick={handleCreateStop}
        />
        <div className="table_container">
          {showCreateForm && (
            <CreateStopForm
              onClose={handleCloseForm}
              onSubmit={handleSubmitStop}
              isEdit={false}
            />
          )}

          <SearchFilter
            searchPlaceholder="search"
            filters={filters}
            onSearchChange={handleSearchChange}
            onFilterChange={handleFilterChange}
            dataDownload={true}
            downloadRows={filteredData}
            downloadColumns={headers
              .filter((header) => header.key !== "actions")
              .map((header) => ({
                key: header.key,
                label: header.value,
              }))}
            downloadFileName="school-stops"
          />

          <Suspense fallback={<div className="loader"></div>}>
            <SchoolDynamicTable
              headers={headers}
              data={filteredData}
              loading={isLoading}
              expandedRow={expandedRow}
              onRowClick={handleRowClick}
              renderExpandedRow={renderExpandedRow}
            />
          </Suspense>
        </div>
      </Box>
      <Footer />
    </Box>
  );
}
